import React, { useEffect, useState } from 'react';

// Clipboard helper that works on HTTP origins (navigator.clipboard is only
// available on HTTPS or localhost). Falls back to a hidden textarea.
const copyText = async (text) => {
  const s = String(text ?? '');
  try {
    if (navigator?.clipboard?.writeText) { await navigator.clipboard.writeText(s); return true; }
  } catch { /* fall through */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = s; ta.setAttribute('readonly', '');
    ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch { return false; }
};

const CONFIG_URL = '/api/v1/ai/revenue-manager/rate-shop-config';

function CompetitorRateSettings({ token, selectedProperty }) {
  const [form, setForm] = useState({
    service_label: 'EASEROOM Rate Data Service',
    roster_endpoint_url: '',
    shop_frequency: 1,
    default_currency: 'INR',
    allowed_ips: '',
    is_enabled: false,
  });
  // Outbound (partner-issued) credentials — write-only; masked on load.
  const [apiKeySet, setApiKeySet] = useState(false);
  const [apiKeyMasked, setApiKeyMasked] = useState('');
  const [secretSet, setSecretSet] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [apiKeyEditing, setApiKeyEditing] = useState(false);
  const [secretInput, setSecretInput] = useState('');
  const [secretEditing, setSecretEditing] = useState(false);
  // Inbound (we-issued) credentials — shown so admin can share with the Service.
  const [inboundKey, setInboundKey] = useState('');
  const [inboundSecret, setInboundSecret] = useState('');
  const [inboundVisible, setInboundVisible] = useState(false);
  const [ipInput, setIpInput] = useState('');
  const [ipList, setIpList] = useState([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');
  // Recent Flow A/B activity (pms_rm_rate_shop_log)
  const [logRows, setLogRows] = useState([]);
  const [logLoading, setLogLoading] = useState(false);
  const [lastInboundAt, setLastInboundAt] = useState(null);

  const origin = window.location.origin;
  const ratesCallbackUrl = `${origin}/api/v1/partner/rate-shop/rates`;
  const resolutionCallbackUrl = `${origin}/api/v1/partner/rate-shop/resolution`;

  const headers = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
  });

  const loadConfig = () => {
    setLoading(true);
    fetch(CONFIG_URL, { headers: headers() })
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (ok && data) {
          setForm({
            service_label: data.service_label || 'EASEROOM Rate Data Service',
            roster_endpoint_url: data.roster_endpoint_url || '',
            shop_frequency: Number(data.shop_frequency) || 1,
            default_currency: data.default_currency || 'INR',
            allowed_ips: data.allowed_ips || '',
            is_enabled: Boolean(data.is_enabled),
          });
          setApiKeySet(Boolean(data.service_api_key_set));
          setApiKeyMasked(data.service_api_key_masked || '');
          setSecretSet(Boolean(data.service_shared_secret_set));
          setInboundKey(data.inbound_api_key || '');
          setInboundSecret(data.inbound_webhook_secret || '');
          setIpList((data.allowed_ips || '').split(',').map((s) => s.trim()).filter(Boolean));
        } else if (data?.error) {
          setError(data.error);
        }
      })
      .catch(() => setError('Network error while loading configuration'))
      .finally(() => setLoading(false));
  };

  const loadLog = () => {
    setLogLoading(true);
    fetch(`${CONFIG_URL.replace('/rate-shop-config', '/rate-shop-log')}?limit=50`, { headers: headers() })
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (ok && data) {
          setLogRows(Array.isArray(data.rows) ? data.rows : []);
          setLastInboundAt(data.last_inbound_at || null);
        }
      })
      .catch(() => { /* log panel is best-effort */ })
      .finally(() => setLogLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadConfig(); loadLog(); }, [token, selectedProperty?.chain_id]);

  const isValidUrl = (val) => { try { new URL(val.trim()); return true; } catch { return false; } };

  const save = async (overrides = {}) => {
    setError(''); setMessage('');
    if (form.roster_endpoint_url.trim() && !isValidUrl(form.roster_endpoint_url)) {
      setError('Partner Roster Endpoint URL must be a valid URL'); return;
    }
    setSaving(true);
    try {
      const body = {
        service_label: form.service_label.trim() || 'EASEROOM Rate Data Service',
        roster_endpoint_url: form.roster_endpoint_url.trim() || null,
        shop_frequency: Number(form.shop_frequency) || 1,
        default_currency: (form.default_currency || 'INR').trim().toUpperCase(),
        allowed_ips: ipList.join(',') || null,
        is_enabled: form.is_enabled,
        ...overrides,
      };
      if (apiKeyEditing && apiKeyInput.trim()) body.service_api_key = apiKeyInput.trim();
      if (secretEditing && secretInput.trim()) body.service_shared_secret = secretInput.trim();
      const res = await fetch(CONFIG_URL, { method: 'POST', headers: headers(), body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) { setError(data?.error || 'Failed to save'); return; }
      setMessage('Competitor Rate API settings saved.');
      setApiKeyEditing(false); setApiKeyInput('');
      setSecretEditing(false); setSecretInput('');
      loadConfig();
    } catch {
      setError('Network error while saving');
    } finally { setSaving(false); }
  };

  const regenerateInbound = async () => {
    if (!window.confirm('Regenerate inbound API key & secret? The Rate Data Service must be updated with the new values or their pushes will be rejected.')) return;
    await save({ regenerate_inbound: true });
  };

  const pushRoster = async () => {
    setPushing(true); setError(''); setMessage('');
    try {
      const res = await fetch(CONFIG_URL.replace('/rate-shop-config', '/roster-push'), { method: 'POST', headers: headers() });
      const data = await res.json();
      if (!res.ok) { setError(data?.error || 'Roster push failed'); return; }
      setMessage(`Roster pushed to the Rate Data Service — ${data.property_count ?? 0} propert${(data.property_count === 1) ? 'y' : 'ies'}, ${data.competitor_count ?? 0} competitor(s). The Service will shop and post rates back shortly.`);
      loadLog();
    } catch {
      setError('Network error during roster push');
    } finally { setPushing(false); }
  };

  const testConnection = async () => {
    setTesting(true); setError(''); setMessage('');
    try {
      const res = await fetch(`${CONFIG_URL}/test`, { method: 'POST', headers: headers() });
      const data = await res.json();
      if (!res.ok) { setError(data?.error || 'Test failed'); return; }
      if (data.reachable) setMessage(`Roster endpoint reachable (HTTP ${data.status}).`);
      else setError(`Roster endpoint not reachable: ${data.error || 'unknown error'}`);
    } catch {
      setError('Network error during test');
    } finally { setTesting(false); }
  };

  const addIp = () => {
    const ip = ipInput.trim();
    if (!ip) return;
    if (!/^[\d.:\w]+$/.test(ip)) { setError('Invalid IP address format'); return; }
    if (ipList.includes(ip)) { setError('IP already in whitelist'); return; }
    setError('');
    setIpList([...ipList, ip]); setIpInput('');
  };
  const removeIp = (ip) => setIpList(ipList.filter((x) => x !== ip));

  const doCopy = async (val, tag) => {
    if (await copyText(val)) { setCopied(tag); setTimeout(() => setCopied(''), 2000); }
  };

  const fieldStyle = { padding: '0.7rem', borderRadius: '6px', border: '1px solid #dcdde1', fontSize: '0.9rem' };
  const roStyle = { ...fieldStyle, flex: 1, background: 'var(--theme-border-light)', color: '#1e40af', fontFamily: 'monospace', fontSize: '0.82rem' };
  const sectionStyle = { background: 'var(--theme-bg-light)', border: '1px solid var(--theme-border)', borderRadius: '8px', padding: '1.2rem 1.4rem', marginTop: '1.5rem' };
  const labelStyle = { fontWeight: 600, fontSize: '0.875rem', color: 'var(--theme-text)' };

  if (loading) return <div className="page-shell" style={{ color: '#6b7280' }}>Loading...</div>;

  return (
    <div className="page-shell">
      <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '0.6rem' }}>
        <span>Settings</span> <span style={{ color: '#cbd5e0' }}>›</span> <span style={{ color: 'var(--theme-primary-dark)', fontWeight: 600 }}>Competitor Rate API</span>
      </div>
      <h2 style={{ color: 'var(--theme-primary-dark)', marginBottom: '0.25rem' }}>Competitor Rate API Settings</h2>
      <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
        Configure the EASEROOM Rate Data Service integration. EASEPMS <b>pushes</b> each property's competitor list to the Service's roster URL, and the Service <b>posts back</b> competitor OTA rates to the callback URLs below.
      </p>

      {error && <div className="error-banner" style={{ marginBottom: '1rem' }}>{error}</div>}
      {message && <div style={{ background: '#f0fdf4', border: '1px solid #86efac', color: '#166534', padding: '0.7rem 1rem', borderRadius: '6px', marginBottom: '1rem', fontSize: '0.9rem' }}>{message}</div>}

      <div style={{ maxWidth: '680px' }}>
        {/* ── Outbound: what WE push to the Service ── */}
        <div style={sectionStyle}>
          <h3 style={{ margin: '0 0 0.1rem', color: '#1e3a5f', fontSize: '1rem' }}>Rate Data Service (Outbound)</h3>
          <p style={{ margin: '0 0 1.2rem', fontSize: '0.82rem', color: '#6b7280' }}>
            The Service provides one fixed roster URL plus an API key &amp; shared secret. EASEPMS uses them to push the competitor roster (Flow A).
          </p>

          <div style={{ display: 'grid', gap: '1.2rem' }}>
            <div style={{ display: 'grid', gap: '0.3rem' }}>
              <label style={labelStyle}>Service Label</label>
              <input value={form.service_label} onChange={(e) => setForm({ ...form, service_label: e.target.value })} placeholder="EASEROOM Rate Data Service" style={fieldStyle} />
            </div>

            <div style={{ display: 'grid', gap: '0.3rem' }}>
              <label style={labelStyle}>Partner Roster Endpoint URL <span style={{ color: '#9ca3af', fontWeight: 400 }}>(where EASEPMS POSTs the roster)</span></label>
              <input value={form.roster_endpoint_url} onChange={(e) => setForm({ ...form, roster_endpoint_url: e.target.value.slice(0, 1000) })} placeholder="https://ratedata.easeroom.co/api/roster" style={fieldStyle} />
            </div>

            {/* Partner API Key */}
            <div style={{ display: 'grid', gap: '0.3rem' }}>
              <label style={labelStyle}>Partner API Key <span style={{ color: '#9ca3af', fontWeight: 400 }}>(sent as X-Api-Key)</span></label>
              {apiKeyEditing ? (
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input value={apiKeyInput} onChange={(e) => setApiKeyInput(e.target.value)} placeholder="Paste the Service-provided API key" style={{ ...fieldStyle, flex: 1, fontFamily: 'monospace' }} />
                  <button onClick={() => { setApiKeyEditing(false); setApiKeyInput(''); }} style={btnGhost}>Cancel</button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input readOnly value={apiKeySet ? apiKeyMasked : 'Not configured'} style={{ ...fieldStyle, flex: 1, background: 'var(--theme-border-light)', fontFamily: 'monospace', color: apiKeySet ? '#1e293b' : '#9ca3af' }} />
                  <button onClick={() => setApiKeyEditing(true)} style={btnGhost}>{apiKeySet ? 'Change' : 'Set Key'}</button>
                </div>
              )}
            </div>

            {/* Partner Shared Secret */}
            <div style={{ display: 'grid', gap: '0.3rem' }}>
              <label style={labelStyle}>Partner Shared Secret <span style={{ color: '#9ca3af', fontWeight: 400 }}>(HMAC signs our outbound pushes)</span></label>
              {secretEditing ? (
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input value={secretInput} onChange={(e) => setSecretInput(e.target.value)} placeholder="Paste the Service-provided shared secret" style={{ ...fieldStyle, flex: 1, fontFamily: 'monospace' }} />
                  <button onClick={() => { setSecretEditing(false); setSecretInput(''); }} style={btnGhost}>Cancel</button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input readOnly value={secretSet ? '•••••••• (configured)' : 'Not configured'} style={{ ...fieldStyle, flex: 1, background: 'var(--theme-border-light)', fontFamily: 'monospace', color: secretSet ? '#1e293b' : '#9ca3af' }} />
                  <button onClick={() => setSecretEditing(true)} style={btnGhost}>{secretSet ? 'Change' : 'Set Secret'}</button>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '1.2rem', flexWrap: 'wrap' }}>
              <div style={{ display: 'grid', gap: '0.3rem' }}>
                <label style={labelStyle}>Shop Frequency / day</label>
                <select value={form.shop_frequency} onChange={(e) => setForm({ ...form, shop_frequency: Number(e.target.value) })} style={{ ...fieldStyle, width: 160 }}>
                  <option value={1}>1× per day</option>
                  <option value={2}>2× per day</option>
                </select>
              </div>
              <div style={{ display: 'grid', gap: '0.3rem' }}>
                <label style={labelStyle}>Default Currency</label>
                <input value={form.default_currency} onChange={(e) => setForm({ ...form, default_currency: e.target.value.slice(0, 3).toUpperCase() })} maxLength={3} style={{ ...fieldStyle, width: 100 }} />
              </div>
              <div style={{ display: 'grid', gap: '0.3rem' }}>
                <label style={labelStyle}>Integration</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', paddingTop: '0.5rem' }}>
                  <input type="checkbox" checked={form.is_enabled} onChange={(e) => setForm({ ...form, is_enabled: e.target.checked })} />
                  Enabled
                </label>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
              <button onClick={() => save()} disabled={saving} className="btn-primary" style={{ padding: '0.65rem 1.6rem' }}>
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
              <button onClick={testConnection} disabled={testing} style={btnSecondary}>
                {testing ? 'Testing...' : 'Test Connection'}
              </button>
              <button onClick={pushRoster} disabled={pushing || !form.is_enabled} title={!form.is_enabled ? 'Enable the integration and Save first' : 'Push the competitor roster to the Rate Data Service now'}
                style={{ padding: '0.65rem 1.3rem', border: 'none', borderRadius: '6px', background: (pushing || !form.is_enabled) ? '#94a3b8' : '#059669', color: '#fff', cursor: (pushing || !form.is_enabled) ? 'default' : 'pointer', fontSize: '0.85rem', fontWeight: 600 }}>
                {pushing ? 'Pushing...' : 'Push Roster Now'}
              </button>
            </div>
          </div>
        </div>

        {/* ── Inbound: what the Service posts back to US ── */}
        <div style={sectionStyle}>
          <h3 style={{ margin: '0 0 0.1rem', color: '#1e3a5f', fontSize: '1rem' }}>Our Callback URLs &amp; Credentials (Inbound)</h3>
          <p style={{ margin: '0 0 1.2rem', fontSize: '0.82rem', color: '#6b7280' }}>
            Share these with the Rate Data Service. They POST competitor rates and resolution acknowledgements here (Flow B), authenticating with the API key &amp; secret we issue below.
          </p>

          {[
            { label: 'Rates Callback URL', url: ratesCallbackUrl, tag: 'rates' },
            { label: 'Resolution Callback URL', url: resolutionCallbackUrl, tag: 'resolution' },
          ].map((c) => (
            <div key={c.tag} style={{ marginBottom: '1.2rem' }}>
              <label style={{ ...labelStyle, display: 'block', marginBottom: '0.3rem' }}>{c.label}</label>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input readOnly value={c.url} style={roStyle} />
                <button onClick={() => doCopy(c.url, c.tag)} style={{ ...btnCopy, background: copied === c.tag ? 'var(--theme-success)' : '#2563eb' }}>
                  {copied === c.tag ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
          ))}

          {/* Inbound API Key */}
          <div style={{ marginBottom: '1.2rem' }}>
            <label style={{ ...labelStyle, display: 'block', marginBottom: '0.3rem' }}>Inbound API Key <span style={{ fontWeight: 400, color: '#6b7280' }}>(Service sends as X-Api-Key)</span></label>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <input readOnly type={inboundVisible ? 'text' : 'password'} value={inboundKey} style={{ ...roStyle, minWidth: 200 }} />
              <button onClick={() => setInboundVisible(!inboundVisible)} style={btnGhost}>{inboundVisible ? 'Hide' : 'Show'}</button>
              <button onClick={() => doCopy(inboundKey, 'inkey')} style={{ ...btnCopy, background: copied === 'inkey' ? 'var(--theme-success)' : '#2563eb' }}>{copied === 'inkey' ? 'Copied!' : 'Copy'}</button>
            </div>
          </div>

          {/* Inbound Webhook Secret */}
          <div style={{ marginBottom: '1.2rem' }}>
            <label style={{ ...labelStyle, display: 'block', marginBottom: '0.3rem' }}>Inbound Webhook Secret <span style={{ fontWeight: 400, color: '#6b7280' }}>(Service HMAC-signs posts with X-Signature)</span></label>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <input readOnly type={inboundVisible ? 'text' : 'password'} value={inboundSecret} style={{ ...roStyle, minWidth: 200 }} />
              <button onClick={() => doCopy(inboundSecret, 'insecret')} style={{ ...btnCopy, background: copied === 'insecret' ? 'var(--theme-success)' : '#2563eb' }}>{copied === 'insecret' ? 'Copied!' : 'Copy'}</button>
              <button onClick={regenerateInbound} disabled={saving} style={{ ...btnGhost, color: '#b45309', borderColor: '#fbbf24' }}>Regenerate</button>
            </div>
          </div>

          {/* Inbound IP whitelist */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ ...labelStyle, display: 'block', marginBottom: '0.3rem' }}>Source IP Whitelist <span style={{ fontWeight: 400, color: '#6b7280' }}>(leave empty to allow any IP)</span></label>
            {ipList.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.6rem' }}>
                {ipList.map((ip) => (
                  <span key={ip} style={{ background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', borderRadius: '20px', padding: '0.2rem 0.75rem', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    {ip}
                    <button onClick={() => removeIp(ip)} style={{ background: 'none', border: 'none', color: 'var(--theme-danger)', cursor: 'pointer', fontWeight: 700, lineHeight: 1, padding: '0 2px', fontSize: '0.9rem' }} title="Remove">×</button>
                  </span>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input value={ipInput} onChange={(e) => setIpInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addIp()} placeholder="e.g. 103.21.58.100" style={{ ...fieldStyle, flex: 1, fontSize: '0.85rem' }} />
              <button onClick={addIp} style={{ ...btnCopy, background: '#059669' }}>+ Add IP</button>
            </div>
          </div>

          <button onClick={() => save()} disabled={saving} className="btn-primary" style={{ padding: '0.55rem 1.4rem', fontSize: '0.875rem' }}>
            {saving ? 'Saving...' : 'Save Inbound Settings'}
          </button>
        </div>

        {/* ── Recent Activity Log (Flow A pushes + Flow B inbound rates) ── */}
        <div style={sectionStyle}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
            <h3 style={{ margin: 0, color: '#1e3a5f', fontSize: '1rem' }}>Recent Activity Log</h3>
            <button onClick={loadLog} disabled={logLoading} style={btnGhost}>{logLoading ? 'Refreshing…' : 'Refresh'}</button>
          </div>
          <p style={{ margin: '0 0 1rem', fontSize: '0.82rem', color: '#6b7280' }}>
            Outbound roster pushes (<b>ROSTER_PUSH</b>) and inbound competitor-rate batches (<b>RATES_INBOUND</b>).
            {lastInboundAt && <> Last rates received: <b>{new Date(lastInboundAt).toLocaleString()}</b>.</>}
          </p>
          {logRows.length === 0 ? (
            <p style={{ color: '#9ca3af', fontSize: '0.88rem', margin: 0 }}>
              No activity yet. Add competitors to a property, then the roster push and inbound rates will appear here.
            </p>
          ) : (
            <div style={{ overflowX: 'auto', border: '1px solid var(--theme-border)', borderRadius: 6 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', fontSize: '0.82rem', minWidth: 640 }}>
                <thead>
                  <tr style={{ background: 'var(--theme-primary)', color: '#fff' }}>
                    <th style={logTh}>Time</th>
                    <th style={logTh}>Direction</th>
                    <th style={logTh}>Property</th>
                    <th style={{ ...logTh, textAlign: 'right' }}>Rows</th>
                    <th style={logTh}>Outcome</th>
                    <th style={logTh}>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {logRows.map((r, i) => {
                    const inbound = r.event_type === 'RATES_INBOUND';
                    const oc = String(r.outcome || '').toUpperCase();
                    const ocColor = oc === 'ACCEPTED' ? '#166534' : oc === 'PARTIAL' ? '#b45309' : '#b91c1c';
                    return (
                      <tr key={i} style={{ borderTop: '1px solid #e2e8f0' }}>
                        <td style={{ ...logTd, whiteSpace: 'nowrap' }}>{new Date(r.created_at).toLocaleString()}</td>
                        <td style={logTd}>
                          <span style={{ fontWeight: 600, color: inbound ? '#2563eb' : '#6d28d9' }}>
                            {inbound ? '⭠ RATES_INBOUND' : '⭢ ROSTER_PUSH'}
                          </span>
                        </td>
                        <td style={logTd}>{r.property_name || <span style={{ color: '#9ca3af' }}>—</span>}</td>
                        <td style={{ ...logTd, textAlign: 'right' }}>{r.row_count ?? '—'}</td>
                        <td style={{ ...logTd, fontWeight: 700, color: ocColor }}>{r.outcome || '—'}</td>
                        <td style={{ ...logTd, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#b91c1c' }} title={r.error_message || ''}>{r.error_message || ''}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const btnGhost = { padding: '0.5rem 0.9rem', border: '1px solid #d1d5db', borderRadius: '6px', background: '#fff', cursor: 'pointer', fontSize: '0.82rem', whiteSpace: 'nowrap' };
const btnSecondary = { padding: '0.65rem 1.3rem', border: '1px solid #2563eb', borderRadius: '6px', background: '#fff', color: '#2563eb', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 };
const btnCopy = { padding: '0.5rem 1rem', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', whiteSpace: 'nowrap' };
const logTh = { padding: '0.45rem 0.55rem', textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap' };
const logTd = { padding: '0.4rem 0.55rem', color: '#1e293b' };

export default CompetitorRateSettings;
